import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, AcademicCapIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/outline';
import recitationDB from '../utils/recitationDB';
import type { RecitationLevel, RecitationStatus } from '../types';

interface RecitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  passageId: string;
}

// 背诵难度级别
const RECITATION_LEVELS = [
  { level: 0, name: '原文', percentage: 0, color: 'bg-blue-600' },
  { level: 1, name: '初级', percentage: 25, color: 'bg-gray-100' },
  { level: 2, name: '中级', percentage: 50, color: 'bg-gray-100' },
  { level: 3, name: '高级', percentage: 75, color: 'bg-gray-100' },
  { level: 4, name: '专家', percentage: 100, color: 'bg-gray-100' },
] as const;

export default function RecitationModal({ isOpen, onClose, title, content, passageId }: RecitationModalProps) {
  // 背诵状态
  const [status, setStatus] = useState<RecitationStatus>('selecting');
  const [currentLevel, setCurrentLevel] = useState<RecitationLevel>(0);
  const [maskedContent, setMaskedContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isWordSelectionMode, setIsWordSelectionMode] = useState(false);
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
  const [allBlanksAnswered, setAllBlanksAnswered] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState<Record<number, string>>({});
  const [, setBlankPositions] = useState<number[]>([]);
  const [currentOptions, setCurrentOptions] = useState<{ position: number; options: string[] } | null>(null);
  const [preloadedOptions, setPreloadedOptions] = useState<Record<number, string[]>>({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{
    accuracy: number;
    message: string;
    color: string;
  } | null>(null);

  const speechSynthesis = useRef<SpeechSynthesis | null>(null);
  const countdownTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      speechSynthesis.current = window.speechSynthesis;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setOriginalContent(content);
      generateMaskedContent(content, 0);
    }
  }, [isOpen, content]);

  // 生成挖空内容
  const generateMaskedContent = (text: string, level: RecitationLevel) => {
    // 重置状态
    setUserAnswers({});
    setAllBlanksAnswered(false);
    setCorrectAnswers({});
    setBlankPositions([]);
    setCurrentOptions(null);
    setFeedback(null);

    // 原文级别，直接显示原文
    if (level === 0) {
      setMaskedContent(text);
      return;
    }

    // 清除标点符号，只保留汉字
    const cleanContent = text.replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '');
    const contentArray = cleanContent.split('');
    
    // 根据难度级别确定挖空比例
    const maskPercentages = [0, 0.25, 0.5, 0.75, 1];
    const maskPercentage = maskPercentages[level];
    
    const totalChars = contentArray.length;
    const charsToMask = Math.floor(totalChars * maskPercentage);
    
    // 创建索引数组并随机打乱
    const indices = Array.from({ length: totalChars }, (_, i) => i);
    const shuffledIndices = shuffleArray(indices);
    const maskIndices = shuffledIndices.slice(0, charsToMask);
    
    // 在原始内容中应用挖空
    const maskedArray = text.split('');
    const newBlankPositions: number[] = [];
    const newCorrectAnswers: Record<number, string> = {};
    
    let cleanIndex = 0;
    for (let i = 0; i < maskedArray.length; i++) {
      const char = maskedArray[i];
      if (/[\u4e00-\u9fa5a-zA-Z0-9]/.test(char)) {
        if (maskIndices.includes(cleanIndex)) {
          maskedArray[i] = '＿';
          newBlankPositions.push(i);
          newCorrectAnswers[i] = char;
        }
        cleanIndex++;
      }
    }
    
    setMaskedContent(maskedArray.join(''));
    setBlankPositions(newBlankPositions);
    setCorrectAnswers(newCorrectAnswers);
    
    // 如果在选词模式，预加载选项
    if (isWordSelectionMode) {
      preloadOptionsForAllBlanks(newBlankPositions);
    }
  };

  // 预加载所有空白的选项
  const preloadOptionsForAllBlanks = async (positions: number[]) => {
    const newPreloadedOptions: Record<number, string[]> = {};
    
    for (const position of positions) {
      try {
        // 检查是否有缓存的选项
        const cachedOptions = await recitationDB.getOptions(passageId, position);
        
        if (cachedOptions) {
          newPreloadedOptions[position] = cachedOptions.options;
        } else {
          // 生成新选项
          const originalChar = getOriginalCharAtPosition(position);
          if (originalChar) {
            const options = generateOptionsForBlank(originalChar);
            newPreloadedOptions[position] = options;
            
            // 保存到数据库
            await recitationDB.saveOptions({
              passage_id: passageId,
              position,
              options,
              original_char: originalChar
            });
          }
        }
      } catch (error) {
        console.error(`为位置 ${position} 生成选项时出错:`, error);
      }
    }
    
    setPreloadedOptions(newPreloadedOptions);
  };

  // 获取指定位置的原始字符
  const getOriginalCharAtPosition = (position: number): string => {
    return originalContent[position] || '';
  };

  // 生成选项
  const generateOptionsForBlank = (correctChar: string): string[] => {
    const options = [correctChar];
    
    // 从原文中获取其他字符作为干扰项
    const cleanContent = originalContent.replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '');
    const availableChars = cleanContent.split('').filter(c => !options.includes(c) && c !== correctChar);
    
    // 添加随机选项直到有4个选项
    while (options.length < 4 && availableChars.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableChars.length);
      const randomChar = availableChars[randomIndex];
      if (!options.includes(randomChar)) {
        options.push(randomChar);
      }
      availableChars.splice(randomIndex, 1);
    }
    
    // 如果选项不够，使用默认字符
    const defaultChars = '的一是在不了有和人这中大为上个国我以要他时来用们生到作地于出就分对成会可主发年动同工也能下过子说产种面而方后多定行学法所民得经十三之进着等部度家电力里如水化高自二理起小物现实加量都两体制机当使点从业本去把性好应开它合还因由其些然前外天政四日那社义事平形相全表间样与关各重新线内数正心反你明看原又么利比或但质气第向道命此变条只没结解问意建月公无系军很情者最立代想已通并提直题党程展五果料象员革位入常文总次品式活设及管特件长求老头基资边流路级少图山统接知较将组见计别她手角期根论运农指几九区强放决西被干做必战先回则任取据处队南给色光门即保治北造百规热领七海口东导器压志世金增争济阶油思术极交受联什认六共权收证改清己美再采转更单风切打白教速花带安场身车例真务具万每目至达走积示议声报斗完类八离华名确才科张信马节话米整空元况今集温传土许步群广石记需段研界拉林律叫且究观越织装影算低持音众书布复容儿须际商非验连断深难近矿千周委素技备半办青省列习响约支般史感劳便团往酸历市克何除消构府称太准精值号率族维划选标写存候毛亲快效斯院查江型眼王按格养易置派层片始却专状育厂京识适属圆包火住调满县局照参红细引听该铁价严龙飞';
    
    while (options.length < 4) {
      const randomIndex = Math.floor(Math.random() * defaultChars.length);
      const randomChar = defaultChars[randomIndex];
      if (!options.includes(randomChar)) {
        options.push(randomChar);
      }
    }
    
    return shuffleArray(options);
  };

  // 打乱数组
  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // 处理空白点击
  const handleBlankClick = async (position: number) => {
    if (!isWordSelectionMode || allBlanksAnswered) return;
    
    // 检查是否有预加载的选项
    if (preloadedOptions[position]) {
      setCurrentOptions({ position, options: preloadedOptions[position] });
    } else {
      // 生成新选项
      const originalChar = getOriginalCharAtPosition(position);
      if (originalChar) {
        const options = generateOptionsForBlank(originalChar);
        setCurrentOptions({ position, options });
        
        // 保存到数据库
        try {
          await recitationDB.saveOptions({
            passage_id: passageId,
            position,
            options,
            original_char: originalChar
          });
        } catch (error) {
          console.error('保存选项失败:', error);
        }
      }
    }
  };

  // 处理选项选择
  const handleOptionSelect = (option: string) => {
    if (!currentOptions) return;
    
    const newUserAnswers = { ...userAnswers, [currentOptions.position]: option };
    setUserAnswers(newUserAnswers);
    
    // 更新显示内容
    const maskedArray = maskedContent.split('');
    maskedArray[currentOptions.position] = option;
    setMaskedContent(maskedArray.join(''));
    
    setCurrentOptions(null);
    
    // 检查是否所有空白都已填写
    const remainingBlanks = maskedArray.filter(char => char === '＿').length;
    if (remainingBlanks === 0) {
      setAllBlanksAnswered(true);
      gradeAnswers(newUserAnswers);
    }
  };

  // 评分
  const gradeAnswers = (answers: Record<number, string>) => {
    let correctCount = 0;
    let totalAnswers = 0;
    
    Object.entries(answers).forEach(([position, answer]) => {
      const pos = parseInt(position);
      totalAnswers++;
      if (answer === correctAnswers[pos]) {
        correctCount++;
      }
    });
    
    const accuracy = totalAnswers > 0 ? Math.round((correctCount / totalAnswers) * 100) : 0;
    
    setFeedback({
      accuracy,
      message: getFeedbackMessage(accuracy),
      color: getFeedbackColor(accuracy)
    });
    
    // 如果准确率 >= 90%，开始倒计时进入下一级别
    if (accuracy >= 90 && currentLevel < 4) {
      startCountdown(3, () => {
        changeLevel(currentLevel + 1 as RecitationLevel);
      });
    } else if (accuracy >= 90 && currentLevel === 4) {
      // 完成最高级别
      startCountdown(3, () => {
        setStatus('completed');
      });
    }
  };

  // 获取反馈消息
  const getFeedbackMessage = (accuracy: number): string => {
    if (accuracy >= 90) return '太棒了！选词完全正确，3秒后将进入下一个难度级别。';
    if (accuracy >= 70) return '非常好！选词大部分正确，继续努力。';
    if (accuracy >= 50) return '继续努力！选词有一半正确，请再次尝试。';
    return '需要更多练习，请再次尝试选词。';
  };

  // 获取反馈颜色
  const getFeedbackColor = (accuracy: number): string => {
    if (accuracy >= 90) return 'bg-green-50 text-green-800';
    if (accuracy >= 70) return 'bg-blue-50 text-blue-800';
    if (accuracy >= 50) return 'bg-yellow-50 text-yellow-800';
    return 'bg-red-50 text-red-800';
  };

  // 开始倒计时
  const startCountdown = (seconds: number, callback: () => void) => {
    setCountdown(seconds);
    countdownTimer.current = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(countdownTimer.current!);
          callback();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 切换难度级别
  const changeLevel = (level: RecitationLevel) => {
    setCurrentLevel(level);
    setFeedback(null);
    setCountdown(null);
    
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    
    // 非原文级别自动进入选词模式
    if (level !== 0) {
      setIsWordSelectionMode(true);
    } else {
      setIsWordSelectionMode(false);
    }
    
    generateMaskedContent(originalContent, level);
  };

  // 播放原文
  const playContent = () => {
    if (!speechSynthesis.current || isPlaying) return;
    
    setIsPlaying(true);
    const utterance = new SpeechSynthesisUtterance(originalContent);
    utterance.lang = 'zh-CN';
    utterance.rate = 0.8;
    
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    
    speechSynthesis.current.speak(utterance);
  };

  // 停止播放
  const stopPlaying = () => {
    if (speechSynthesis.current) {
      speechSynthesis.current.cancel();
    }
    setIsPlaying(false);
  };

  // 重试当前级别
  const retryCurrentLevel = () => {
    setFeedback(null);
    setCountdown(null);
    setAllBlanksAnswered(false);
    setUserAnswers({});
    setCurrentOptions(null);
    
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    
    generateMaskedContent(originalContent, currentLevel);
  };

  // 退出背诵
  const handleClose = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }
    if (speechSynthesis.current) {
      speechSynthesis.current.cancel();
    }
    onClose();
  };

  // 渲染内容
  const renderContent = () => {
    return maskedContent.split('').map((char, index) => {
      if (char === '＿') {
        if (userAnswers[index] !== undefined) {
          // 已填写的空白
          if (allBlanksAnswered) {
            // 已评分
            const isCorrect = userAnswers[index] === correctAnswers[index];
            return (
              <span
                key={index}
                className={`inline-block min-w-[1em] text-center px-1 mx-0.5 rounded ${
                  isCorrect 
                    ? 'bg-green-200 border border-green-400 text-green-800' 
                    : 'bg-red-200 border border-red-400 text-red-800'
                }`}
              >
                {isCorrect ? userAnswers[index] : correctAnswers[index]}
                {!isCorrect && (
                  <span className="text-red-500 line-through ml-1">
                    {userAnswers[index]}
                  </span>
                )}
              </span>
            );
          } else {
            // 未评分
            return (
              <span
                key={index}
                className="inline-block min-w-[1em] text-center px-1 mx-0.5 rounded bg-blue-200 border border-blue-400 text-blue-800"
              >
                {userAnswers[index]}
              </span>
            );
          }
        } else {
          // 未填写的空白
          return (
            <span
              key={index}
              className="inline-block min-w-[1em] text-center px-1 mx-0.5 rounded bg-gray-200 border border-dashed border-gray-400 text-gray-600 cursor-pointer hover:bg-gray-300"
              onClick={() => handleBlankClick(index)}
            >
              ＿
            </span>
          );
        }
      } else {
        // 普通字符
        return (
          <span key={index} className="inline-block">
            {char}
          </span>
        );
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            <AcademicCapIcon className="h-6 w-6 text-amber-600 mr-2" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          
          {/* 难度级别按钮 */}
          <div className="flex gap-2">
            {RECITATION_LEVELS.map(({ level, name, percentage }) => (
              <button
                key={level}
                onClick={() => changeLevel(level as RecitationLevel)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  currentLevel === level
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {name} ({percentage}%)
              </button>
            ))}
          </div>
          
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 主要内容区域 */}
        <div className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <div className="max-w-4xl w-full">
            <div className="bg-gray-50 p-8 rounded-lg text-2xl leading-relaxed whitespace-pre-wrap">
              {renderContent()}
            </div>
          </div>
        </div>

        {/* 选项模态框 */}
        {currentOptions && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-4 shadow-lg">
              <div className="flex gap-2">
                {currentOptions.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleOptionSelect(option)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-lg font-medium"
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 底部控制区域 */}
        <div className="p-4 border-t flex justify-center">
          <div className="flex items-center space-x-4">
            {currentLevel === 0 ? (
              // 原文级别显示播放按钮
              <>
                {!isPlaying ? (
                  <button
                    onClick={playContent}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    <SpeakerWaveIcon className="h-5 w-5" />
                    <span>播放原文</span>
                  </button>
                ) : (
                  <button
                    onClick={stopPlaying}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    <SpeakerXMarkIcon className="h-5 w-5" />
                    <span>停止播放</span>
                  </button>
                )}
              </>
            ) : (
              // 其他级别显示选词模式按钮
              <>
                {!isWordSelectionMode ? (
                  <button
                    onClick={() => setIsWordSelectionMode(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    <AcademicCapIcon className="h-5 w-5" />
                    <span>开始选词背诵</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsWordSelectionMode(false)}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    <XMarkIcon className="h-5 w-5" />
                    <span>退出选词模式</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* 反馈区域 */}
        {feedback && (
          <div className="p-4 border-t bg-gray-50">
            <div className={`p-4 rounded-lg ${feedback.color}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <p className="font-medium">{feedback.message}</p>
                  {countdown !== null && (
                    <span className="ml-2 bg-blue-600 text-white px-2 py-1 rounded-full text-sm font-bold">
                      {countdown}
                    </span>
                  )}
                </div>
                <span className="text-lg font-bold">{feedback.accuracy}%</span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
                <div 
                  className="h-2.5 rounded-full bg-green-600" 
                  style={{ width: `${feedback.accuracy}%` }}
                />
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={retryCurrentLevel}
                  className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded text-sm"
                >
                  再来一次
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 完成模态框 */}
        {status === 'completed' && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-4">恭喜您完成背诵！</h2>
                <p className="text-lg mb-6">您已成功掌握了这篇内容，继续保持！</p>
                
                <div className="mb-6 text-left">
                  <h3 className="font-medium mb-2">原文</h3>
                  <div className="bg-gray-50 p-4 rounded-lg text-lg leading-relaxed whitespace-pre-wrap">
                    {originalContent}
                  </div>
                </div>
                
                <button
                  onClick={handleClose}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
                >
                  完成背诵
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
